/**
 * Simulador.jsx — Wizard standalone /simulador-multas
 * Anónimo, sin login, sin email. Crea un slug compartible al final.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, AlertTriangle, Calculator, Info, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import MultaResultCard from "@/components/MultaResultCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HALLAZGOS_OPCIONES = [
  { id: "sin_politica_privacidad", label: "No tengo política de privacidad publicada (o es muy básica)" },
  { id: "tracker_sin_consentimiento", label: "Uso Google Analytics, Meta Pixel u otros trackers" },
  { id: "cookies_sin_consentimiento", label: "No tengo banner de cookies o se activan antes del consentimiento" },
  { id: "transferencia_internacional_sin_dpa", label: "Uso herramientas como Mailchimp, AWS, Stripe (transferencias internacionales)" },
  { id: "datos_sensibles_sin_base_legal", label: "Mi sitio recolecta datos de salud, biométricos u otros datos sensibles" },
  { id: "datos_menores_sin_base_legal", label: "Mi sitio recolecta datos de menores de edad" },
  { id: "no_responde_arcop", label: "No tengo proceso para responder solicitudes de datos en plazo (ARCOP)" },
];

const INGRESOS_OPCIONES = [
  { id: "<100M", label: "Menos de $100 millones CLP" },
  { id: "100-500M", label: "Entre $100 millones y $500 millones" },
  { id: "500-2000M", label: "Entre $500 millones y $2.000 millones" },
  { id: ">2000M", label: "Más de $2.000 millones" },
];

export default function Simulador() {
  const [step, setStep] = useState(0);
  const [esPyme, setEsPyme] = useState(null);
  const [reincidente, setReincidente] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [ingresoRango, setIngresoRango] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [slug, setSlug] = useState(null);

  const needsIngresos = esPyme === false && reincidente === true;
  const totalSteps = useMemo(() => (needsIngresos ? 4 : 3), [needsIngresos]);

  const toggleChecklist = (id) => {
    setChecklist((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/simulador-multas`, {
        es_pyme: !!esPyme,
        reincidente: !!reincidente,
        hallazgos_checklist: checklist,
        ingreso_anual_rango: needsIngresos ? ingresoRango : null,
      });
      setResultado(data.resultado);
      setSlug(data.slug);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (resultado) {
    return (
      <ResultView resultado={resultado} slug={slug} />
    );
  }

  const canAdvance = () => {
    if (step === 0) return esPyme !== null;
    if (step === 1) return reincidente !== null;
    if (step === 2) return true; // checklist puede estar vacío (asume "leve")
    if (step === 3) return ingresoRango !== null;
    return false;
  };

  const next = () => {
    if (step === 1 && !needsIngresos && step + 1 === 2) {
      setStep(2);
      return;
    }
    if (step === 2 && !needsIngresos) {
      submit();
      return;
    }
    if (step === 3) {
      submit();
      return;
    }
    setStep(step + 1);
  };

  const back = () => setStep(Math.max(0, step - 1));

  return (
    <section data-testid="simulador-page">
      <SEO
        title="Simulador de multas Ley 21.719 — Estima tu exposición gratis · Ley de Datos Chile"
        description="Calcula gratis tu exposición a multas bajo la nueva Ley N° 21.719 de Protección de Datos Personales. Sin login. Educativo, basado en los rangos legales de la Agencia."
        canonical="https://leydedatoschile.com/simulador-multas"
        keywords="simulador multas Ley 21.719, calculadora sanciones protección datos Chile, multas APDP"
      />

      {/* Hero dark */}
      <div className="relative bg-ink-950 text-white overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 pb-12 lg:pt-20">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-5">
            <Calculator className="w-3 h-3" />
            Simulador gratuito · sin login
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-[1.05] tracking-tight">
            ¿Cuánto te podría <em className="italic text-rose-300">costar</em> incumplir la Ley 21.719?
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
            4 preguntas rápidas. Sin login, sin email. Resultado anónimo y compartible.
          </p>
          <div className="mt-5 rounded-2xl bg-amber-500/10 border border-amber-400/20 px-4 py-3 text-sm text-amber-100 flex items-start gap-2.5">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
            <span>Solo cubre <strong className="text-amber-200">riesgo digital</strong> (sitio web, cookies, formularios, terceros). No audita RRHH ni bases internas. Educativo · basado en Art. 34 bis/ter/quáter.</span>
          </div>
        </div>
      </div>

      <div className="px-6 py-10 sm:py-14">
        <div className="max-w-3xl mx-auto">
          {/* Progress */}
          <div className="mb-7">
            <div className="flex items-center justify-between mb-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
              <span>Paso {step + 1} / {totalSteps}</span>
              <span>{Math.round(((step + 1) / totalSteps) * 100)}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    i <= step ? "bg-blue-700" : "bg-slate-200"
                  }`}
                />
              ))}
            </div>
          </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.25 }}
            className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8"
            data-testid={`step-${step}`}
          >
            {step === 0 && (
              <Question
                title="¿Tu empresa califica como PYME bajo la Ley 20.416?"
                help="PYME: hasta 100.000 UF de ventas anuales. Si no sabes, marca 'No estoy seguro' y se asume conservador."
                opts={[
                  { v: true, label: "Sí, somos PYME" },
                  { v: false, label: "No, somos una empresa más grande" },
                  { v: true, label: "No estoy seguro (asumir PYME como conservador)" },
                ]}
                value={esPyme}
                onChange={setEsPyme}
                testid="q-pyme"
              />
            )}

            {step === 1 && (
              <Question
                title="¿Tu empresa ha tenido sanciones de protección de datos en los últimos 5 años?"
                help="La reincidencia dentro de 5 años puede triplicar la multa o aplicar % de ingresos."
                opts={[
                  { v: false, label: "Es la primera vez (sin antecedentes)" },
                  { v: true, label: "Sí, ha tenido sanciones previas" },
                ]}
                value={reincidente}
                onChange={setReincidente}
                testid="q-reincidente"
              />
            )}

            {step === 2 && (
              <div>
                <h2 className="font-display text-2xl sm:text-3xl text-slate-900 tracking-tight">Marca lo que aplica a tu sitio</h2>
                <p className="mt-2 text-sm text-slate-500">Puedes marcar varias. Mostraremos la categoría de mayor severidad.</p>
                <div className="mt-6 space-y-2">
                  {HALLAZGOS_OPCIONES.map((o) => {
                    const active = checklist.includes(o.id);
                    return (
                      <label
                        key={o.id}
                        data-testid={`checklist-${o.id}`}
                        className={`flex items-start gap-3 cursor-pointer p-3 rounded-xl border-2 transition ${
                          active ? "border-blue-700 bg-blue-50/40" : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleChecklist(o.id)}
                          className="mt-1 w-5 h-5 accent-blue-700 shrink-0"
                        />
                        <span className="text-sm text-slate-800">{o.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && needsIngresos && (
              <Question
                title="¿Cuál es el rango de ingresos anuales aproximado?"
                help="Como no eres PYME y eres reincidente, la ley permite calcular % sobre ingresos. Usamos el punto medio del rango."
                opts={INGRESOS_OPCIONES.map((o) => ({ v: o.id, label: o.label }))}
                value={ingresoRango}
                onChange={setIngresoRango}
                testid="q-ingresos"
              />
            )}

            <div className="mt-8 flex items-center justify-between gap-3">
              <button
                onClick={back}
                disabled={step === 0}
                data-testid="step-back"
                className="h-11 px-4 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold inline-flex items-center gap-2 disabled:opacity-40 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Atrás
              </button>
              <button
                onClick={next}
                disabled={!canAdvance() || loading}
                data-testid="step-next"
                className="h-11 px-5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
              >
                {loading ? "Calculando…" : (step === totalSteps - 1 ? <>Ver mi resultado <Sparkles className="w-4 h-4" /></> : <>Siguiente <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

function Question({ title, help, opts, value, onChange, testid }) {
  return (
    <div>
      <h2 className="font-display text-2xl sm:text-3xl text-slate-900 tracking-tight leading-tight">{title}</h2>
      {help && <p className="mt-2 text-sm text-slate-500 leading-relaxed">{help}</p>}
      <div className="mt-6 space-y-2">
        {opts.map((o, i) => {
          const active = value === o.v;
          return (
            <button
              key={i}
              onClick={() => onChange(o.v)}
              data-testid={`${testid}-${i}`}
              className={`w-full text-left p-4 rounded-xl border-2 transition ${
                active ? "border-blue-700 bg-blue-50/40 text-slate-900 font-semibold" : "border-slate-200 hover:border-slate-300 text-slate-800"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultView({ resultado, slug }) {
  const pageUrl = `https://leydedatoschile.com/multas/${slug}`;
  const shareText = encodeURIComponent("Estimé mi exposición a la Ley 21.719 de Protección de Datos. Mira el detalle:");
  return (
    <section>
      <SEO
        title="Tu exposición estimada — Simulador Ley 21.719"
        description="Resultado de la simulación de multas Ley 21.719"
        canonical={pageUrl}
      />

      <div className="relative bg-ink-950 text-white overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 pt-14 pb-10">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-4">
            <Sparkles className="w-3 h-3" /> Simulación completada
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-white leading-[1.05] tracking-tight">
            Tu exposición <em className="italic text-blue-300">estimada</em>.
          </h1>
        </div>
      </div>

      <div className="px-6 py-10 sm:py-14">
        <div className="max-w-3xl mx-auto">
          <MultaResultCard resultado={resultado} />

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
              <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-3">Compartir</p>
              <div className="flex flex-wrap gap-2">
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noopener noreferrer" className="h-10 px-4 rounded-full bg-[#0a66c2] hover:bg-[#0958ad] text-white text-sm font-semibold inline-flex items-center transition-colors">LinkedIn</a>
                <a href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(pageUrl)}`} target="_blank" rel="noopener noreferrer" className="h-10 px-4 rounded-full bg-[#25d366] hover:bg-[#20bd5b] text-white text-sm font-semibold inline-flex items-center transition-colors">WhatsApp</a>
                <button onClick={() => navigator.clipboard?.writeText(pageUrl)} className="h-10 px-4 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold transition-colors">Copiar link</button>
              </div>
            </div>

            <div className="rounded-2xl bg-ink-950 text-white p-5 sm:p-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
              <div className="relative">
                <p className="font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-2 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> No es auditoría
                </p>
                <p className="text-sm text-white/80 leading-relaxed">Para reducir tu exposición real, genera tus documentos legales con IA en minutos.</p>
                <Link to="/modulo-pro" className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors">Ver Módulo Pro <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
